/**
 * High level router.
 *
 * All legacy routes have been archived to src/app/pages/_ARCHIVED_LEGACY_UI
 * This now only handles auth routes and modern UI routes.
 */

import React, { lazy, Suspense, useEffect, useRef } from "react";
import {Redirect, Route, Switch, withRouter} from "react-router-dom";
import {shallowEqual, useSelector} from "react-redux";
import ErrorsPage from "../pages/errors/ErrorsPage";
import LogoutPage from "../pages/auth/Logout";
import * as routerHelpers from "../router/RouterHelpers";
import AuthPage from "../pages/auth/AuthPage";
import OAuthContinue from "../pages/auth/OAuthContinue";
import FullPageLoadingModal from "../../components/ui/FullPageLoadingModal";

const CaesarGPT = lazy(() => import("../pages/home/CaesarGPT/CaesarGPT"));

// Modern Layout Pages
const DashboardModern = lazy(() =>
  import("../pages/home/Dashboard/DashboardWrapper")
);
const ViewTicketsModern = lazy(() =>
  import("../pages/home/Administration/Tickets/ViewTickets/ViewTicketsWrapper")
);
const ViewAccountsModern = lazy(() =>
  import("../pages/home/Administration/Accounts/ViewAccounts/ViewAccountsWrapper")
);
const AccountDetailsModern = lazy(() =>
  import("../pages/home/Customer_Lookup/account_details/AccountDetailsWrapper")
);
const EditAccountModern = lazy(() =>
  import("../pages/home/Customer_Lookup/account_details/EditAccount/EditAccountWrapper")
);
const TicketDetails = lazy(() =>
  import("../pages/home/Administration/Tickets/TicketDetails/TicketDetails")
);
const ProfileModern = lazy(() =>
  import("../pages/home/Profile/ProfileWrapper")
);
const InboxPage = lazy(() =>
  import("../pages/home/Inbox/InboxWrapper")
);
const SettingsModern = lazy(() =>
  import("../pages/home/Administration/Settings/SettingsWrapper")
);
const ProductCatalog = lazy(() =>
  import("../pages/home/Administration/Products/ProductCatalog/ProductCatalogWrapper")
);
const ProductKeys = lazy(() =>
  import("../pages/home/Administration/Products/ProductKeys/ProductKeysWrapper")
);
const ServiceAddresses = lazy(() =>
  import("../pages/home/Administration/ServiceAddresses/ServiceAddressesWrapper")
);
const KnowledgeBase = lazy(() =>
  import("../pages/home/KnowledgeBase/KnowledgeBaseWrapper")
);
const AccountNetworkVisualizer = lazy(() =>
  import("../pages/home/Customer_Lookup/account_details/AccountNetworkVisualizer")
);

// M365 Admin Tools (per-account security audit dashboard)
const M365AdminTools = lazy(() =>
  import("../pages/home/Customer_Lookup/M365AdminTools/M365AdminToolsWrapper")
);

// Google Workspace Admin Tools (per-account Workspace admin via DWD)
const GoogleAdminTools = lazy(() =>
  import("../pages/home/Customer_Lookup/GoogleAdminTools/GoogleAdminToolsWrapper")
);

// Projects Module
const SaasProducts = lazy(() =>
  import("../pages/home/Projects/SaasProducts/SaasProductsWrapper")
);
const SaasProductDetail = lazy(() =>
  import("../pages/home/Projects/SaasProducts/SaasProductDetailWrapper")
);
const ProjectsList = lazy(() =>
  import("../pages/home/Projects/ProjectsList/ProjectsListWrapper")
);
const ProjectsToday = lazy(() =>
  import("../pages/home/Projects/Today/TodayWrapper")
);
const ProjectDetail = lazy(() =>
  import("../pages/home/Projects/ProjectsList/ProjectDetailWrapper")
);
const MyTasks = lazy(() =>
  import("../pages/home/Projects/MyTasks/MyTasksWrapper")
);
const Tasks = lazy(() =>
  import("../pages/home/Tasks/TasksWrapper")
);
const UnifiedCalendar = lazy(() =>
  import("../pages/home/Scheduling/UnifiedCalendarWrapper")
);
const TimeOffRequests = lazy(() =>
  import("../pages/home/Scheduling/TimeOffRequestsWrapper")
);
const CompanyHolidays = lazy(() =>
  import("../pages/home/Scheduling/CompanyHolidaysWrapper")
);
const ViewAgentsModern = lazy(() =>
  import("../pages/home/Administration/Agents/ViewAgents/ViewAgentsWrapper")
);
const InventoryPage = lazy(() =>
  import("../pages/home/Inventory/InventoryWrapper")
);

// Expenses Module
const ViewExpenses = lazy(() =>
  import("../pages/home/Administration/Expenses/ViewExpensesWrapper")
);

// Billing Module
const ViewInvoices = lazy(() =>
  import("../pages/home/Billing/Invoices/ViewInvoicesWrapper")
);

// Audit Logs (system-wide audit trail)
const AuditLogs = lazy(() =>
  import("../pages/home/Administration/AuditLogs/AuditLogsWrapper")
);

// Shared/Public Pages (no auth required)
const SharedTaskPage = lazy(() =>
  import("../pages/shared/SharedTaskPage")
);
const SharedProjectPage = lazy(() =>
  import("../pages/shared/SharedProjectPage")
);
const SharePage = lazy(() =>
  import("../pages/shared/SharePage")
);
const ShareDownloadRedirect = lazy(() =>
  import("../pages/shared/ShareDownloadRedirect")
);
const ConsentPage = lazy(() =>
  import("../pages/shared/ConsentPage")
);
const EstimatePage = lazy(() =>
  import("../pages/shared/EstimatePage")
);
const isEstimatesHost =
  typeof window !== "undefined" && window.location.hostname.startsWith("estimates.");
const WorkbenchGuestPage = lazy(() =>
  import("../pages/shared/WorkbenchGuestPage")
);
const isWorkbenchGuestHost =
  typeof window !== "undefined" && window.location.hostname.startsWith("workbench.");

// Sales Module
const ViewLeads = lazy(() =>
  import("../pages/home/Administration/Leads/ViewLeads/ViewLeadsWrapper")
);
const LeadDetails = lazy(() =>
  import("../pages/home/Administration/Leads/LeadDetails/LeadDetailsWrapper")
);
const ViewOffers = lazy(() =>
  import("../pages/home/Administration/Leads/Offers/ViewOffersWrapper")
);
const ViewBids = lazy(() =>
  import("../pages/home/Administration/Bids/ViewBids/ViewBidsWrapper")
);
const BidDetails = lazy(() =>
  import("../pages/home/Administration/Bids/BidDetails/BidDetailsWrapper")
);
const HigherGovDiscovery = lazy(() =>
  import("../pages/home/Administration/HigherGov/HigherGovDiscoveryWrapper")
);
const Orders = lazy(() =>
  import("../pages/home/Sales/Orders/OrdersWrapper")
);
const Quotes = lazy(() =>
  import("../pages/home/Sales/Quotes/QuotesWrapper")
);

// Help Module
const HelpPage = lazy(() =>
  import("../pages/home/Help/HelpWrapper")
);

// Monitoring Module
const Periscope = lazy(() =>
  import("../pages/home/Monitoring/PeriscopeWrapper")
);
const RMM = lazy(() =>
  import("../pages/home/RMM/RMMWrapper")
);
const Sonar = lazy(() =>
  import("../pages/home/Sonar/SonarWrapper")
);
const SonarAdmin = lazy(() =>
  import("../pages/home/Sonar/admin/SonarAdminWrapper")
);
const SonarNodeDetail = lazy(() =>
  import("../pages/home/Sonar/NodeDetailWrapper")
);
const SonarDeviceDetail = lazy(() =>
  import("../pages/home/Sonar/DeviceDetailWrapper")
);
const SonarDockerServers = lazy(() =>
  import("../pages/home/Sonar/docker/DockerServersWrapper")
);
const SonarDockerHostDetail = lazy(() =>
  import("../pages/home/Sonar/docker/DockerHostDetailWrapper")
);
const SonarKong = lazy(() =>
  import("../pages/home/Sonar/kong/KongDashboardWrapper")
);
const SonarComputeStub = lazy(() =>
  import("../pages/home/Sonar/compute/ComputeComingSoonWrapper")
);
const SonarPhysicalDetail = lazy(() =>
  import("../pages/home/Sonar/compute/PhysicalServerDetailWrapper")
);
const SonarStorageDetail = lazy(() =>
  import("../pages/home/Sonar/compute/StorageArrayDetailWrapper")
);
const SonarPower = lazy(() =>
  import("../pages/home/Sonar/power/PowerDashboardWrapper")
);
const SonarVirtualizationDetail = lazy(() =>
  import("../pages/home/Sonar/compute/HypervisorHostDetailWrapper")
);
const SonarOspDashboard = lazy(() =>
  import("../pages/home/Sonar/fiber/OspDashboardWrapper")
);
const SonarAppPage = lazy(() =>
  import("../pages/home/Sonar/app/SonarAppPageWrapper")
);
const SonarFleetOverview = lazy(() =>
  import("../pages/home/Sonar/overview/SonarFleetOverviewWrapper")
);

// Maps app (ITP Fiber outside plant + CRM/monitoring layers)
const MapsApp = lazy(() =>
  import("../pages/home/Maps/MapsWrapper")
);

// Maps / ITP Fiber inventory pages: strand tables, splice editor, circuits
const MapsCableDetail = lazy(() => import("../pages/home/Maps/fiber/wrapped").then((m) => ({ default: m.CableDetailPage })));
const MapsClosureSplices = lazy(() => import("../pages/home/Maps/fiber/wrapped").then((m) => ({ default: m.ClosureSplicesPage })));
const MapsSpliceSchedulePrint = lazy(() => import("../pages/home/Maps/fiber/SpliceSchedulePrint"));
const MapsCircuitsList = lazy(() => import("../pages/home/Maps/fiber/wrapped").then((m) => ({ default: m.CircuitsListPage })));
const MapsCircuitDetail = lazy(() => import("../pages/home/Maps/fiber/wrapped").then((m) => ({ default: m.CircuitDetailPage })));

// AI Usage Analytics
const AIUsageAnalytics = lazy(() =>
  import("../pages/home/Administration/Settings/AIUsageAnalytics/AIUsageAnalyticsWrapper")
);

// Tickets Analytics
const TicketsAnalytics = lazy(() =>
  import("../pages/home/Analytics/TicketsAnalytics/TicketsAnalyticsWrapper")
);

// AI Agents (workflow automation builder)
const AIAgents = lazy(() =>
  import("../pages/home/AIAgents/AIAgentsWrapper")
);

const ITPAI = lazy(() =>
  import("../pages/home/ITPAI/ITPAIWrapper")
);

// Accounting (owners-only books, reports and AI insights)
const Accounting = lazy(() =>
  import("../pages/home/Accounting/AccountingWrapper")
);

// Workbench (shared remote dev environment / web-based Claude Code)
const Workbench = lazy(() =>
  import("../pages/home/Workbench/WorkbenchWrapper")
);

// Vault (shared infrastructure credentials -- unified browse over the
// existing asset_tracking credentials table)
const Vault = lazy(() =>
  import("../pages/home/Vault/VaultWrapper")
);

// Drive (shared file-share inbox — staff side of the public /share drop page)
const Drive = lazy(() =>
  import("../pages/home/Drive/DriveWrapper")
);

// Photos (reference photo library — many-to-many associations to accounts,
// network locations, equipment locations, and assets)
const Photos = lazy(() =>
  import("../pages/home/Photos/PhotosWrapper")
);

// Gather (recordings + transcripts + settings for the internal WorkAdventure
// virtual office at gather.itp360.com). Admin-only; each wrapper carries its
// own AdminGate to match the API's require_admin gate.
const GatherRecordings = lazy(() =>
  import("../pages/home/Gather/GatherWrapper")
);
const GatherRecordingDetail = lazy(() =>
  import("../pages/home/Gather/GatherDetailWrapper")
);
const GatherSearch = lazy(() =>
  import("../pages/home/Gather/GatherSearchWrapper")
);
const GatherSettings = lazy(() =>
  import("../pages/home/Gather/GatherSettingsWrapper")
);
const GatherAccessLog = lazy(() =>
  import("../pages/home/Gather/GatherAccessLogWrapper")
);
// ITP University (staff training)
const UniversityHome = lazy(() =>
  import("../pages/home/University/UniversityWrapper")
);
const UniversityLesson = lazy(() =>
  import("../pages/home/University/LessonWrapper")
);
const UniversityAdmin = lazy(() =>
  import("../pages/home/University/admin/UniversityAdminWrapper")
);

// ISP Services Module
const IPAMDashboard = lazy(() =>
  import("../pages/home/ISP/IPAMDashboard/IPAMDashboardWrapper")
);
const OLTManagement = lazy(() =>
  import("../pages/home/ISP/FiberSMB/OLTManagement/OLTManagementWrapper")
);
const ONTManagement = lazy(() =>
  import("../pages/home/ISP/FiberSMB/ONTManagement/ONTManagementWrapper")
);
const FiberBuildings = lazy(() =>
  import("../pages/home/ISP/FiberSMB/FiberBuildings/FiberBuildingsWrapper")
);
const ISPConfiguration = lazy(() =>
  import("../pages/home/ISP/Configuration/ConfigurationWrapper")
);

export const Routes = withRouter(({history}) => {
  const {isAuthorized} = useSelector(
    ({auth}) => ({
      isAuthorized: auth.user != null,
    }),
    shallowEqual
  );

  const reloadRestoreRef = useRef(false);

  useEffect(() => {
    if (!isAuthorized || reloadRestoreRef.current) {
      return undefined;
    }

    reloadRestoreRef.current = true;

    if (typeof window === "undefined") {
      return undefined;
    }

    const navEntries =
      typeof window.performance !== "undefined" && typeof window.performance.getEntriesByType === "function"
        ? window.performance.getEntriesByType("navigation")
        : null;
    const entryType = navEntries?.[0]?.type;
    const legacyType = window.performance?.navigation?.type;
    const isReload =
      entryType === "reload" ||
      entryType === 1 ||
      legacyType === 1;

    if (!isReload) {
      return undefined;
    }

    const lastLocation = routerHelpers.getLastLocation();
    if (!lastLocation) {
      return undefined;
    }

    const currentLocation = `${history.location.pathname}${history.location.search || ""}${history.location.hash || ""}`;
    if (lastLocation !== currentLocation) {
      history.replace(lastLocation);
    }

    routerHelpers.forgotLastLocation();
    return undefined;
  }, [history, isAuthorized]);

  useEffect(() => {
    if (!isAuthorized) {
      return undefined;
    }

    const persistCurrentLocation = () => {
      routerHelpers.saveLastLocation(history.location);
    };

    persistCurrentLocation();
    const unlisten = history.listen((location) => {
      routerHelpers.saveLastLocation(location);
    });

    const handleBeforeUnload = () => {
      persistCurrentLocation();
    };

    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      unlisten();
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [history, isAuthorized]);

  // estimates.itp360.com serves ONLY prospect estimate links
  // (estimates.itp360.com/<token>), never the CRM itself. It is a second custom
  // domain on the same Cloudflare Pages project, so this check is the split.
  if (isEstimatesHost) {
    return (
      <Switch>
        <Route path="/:token">
          <Suspense fallback={<FullPageLoadingModal />}>
            <EstimatePage />
          </Suspense>
        </Route>
        <Route>
          <Suspense fallback={<FullPageLoadingModal />}>
            <EstimatePage />
          </Suspense>
        </Route>
      </Switch>
    );
  }

  // workbench.itp360.com is for guests a chat was shared with
  // (workbench.itp360.com/<one-time code>). Same split as estimates above:
  // no CRM routes, no login redirect.
  if (isWorkbenchGuestHost) {
    return (
      <Switch>
        <Route path="/:code">
          <Suspense fallback={<FullPageLoadingModal />}>
            <WorkbenchGuestPage />
          </Suspense>
        </Route>
        <Route>
          <Suspense fallback={<FullPageLoadingModal />}>
            <WorkbenchGuestPage />
          </Suspense>
        </Route>
      </Switch>
    );
  }

  return (
    <>
      <Switch>
        {/* Public routes - no auth required */}
        <Route path="/shared/task/:token">
          <Suspense fallback={<FullPageLoadingModal />}>
            <SharedTaskPage />
          </Suspense>
        </Route>
        <Route path="/shared/project/:token">
          <Suspense fallback={<FullPageLoadingModal />}>
            <SharedProjectPage />
          </Suspense>
        </Route>
        <Route path="/share/d/:token">
          <Suspense fallback={<FullPageLoadingModal />}>
            <ShareDownloadRedirect />
          </Suspense>
        </Route>
        <Route exact path="/share">
          <Suspense fallback={<FullPageLoadingModal />}>
            <SharePage />
          </Suspense>
        </Route>
        {/* Customer data-access consent: the customer approves, denies or
            revokes ITP access to e.g. their mailbox. No CRM login. */}
        <Route path="/consent/:token">
          <Suspense fallback={<FullPageLoadingModal />}>
            <ConsentPage />
          </Suspense>
        </Route>
        {/* Estimate: a prospect reads an ITP 360 estimate and can pick an
            option. No CRM login; the token is the credential. */}
        <Route path="/estimate/:token">
          <Suspense fallback={<FullPageLoadingModal />}>
            <EstimatePage />
          </Suspense>
        </Route>

        {/* OAuth "are you already signed in here?" check, sent by the
            authorization server on api.itpscorp.com. Deliberately ABOVE the
            auth gate: it reads localStorage itself and bounces back to the
            API either way, so the app's redirect to /auth/login must not get
            it first. Not lazy-loaded - a chunk fetch here is one more thing
            between the user and the app they clicked sign-in from. */}
        <Route exact path="/oauth/continue">
          <OAuthContinue />
        </Route>

        {!isAuthorized ? (
          /* Render auth page when user at `/auth` and not authorized. */
          <AuthPage />
        ) : (
          /* Otherwise redirect to root page (`/`) */
          <Redirect from="/auth" to="/dashboard" />
        )}

        <Route path="/error" component={ErrorsPage} />
        <Route path="/logout" component={LogoutPage} />

        {!isAuthorized && (
          /* Redirect to `/auth` when user is not authorized */
          <Redirect to="/auth/login" />
        )}

        {/* Modern UI Routes - All use ModernLayout */}
        {isAuthorized && (
          <Route path="/dashboard">
            <Suspense fallback={<FullPageLoadingModal />}>
              <DashboardModern />
            </Suspense>
          </Route>
        )}

        {isAuthorized && (
          <Route path="/product-catalog">
            <Suspense fallback={<FullPageLoadingModal />}>
              <ProductCatalog />
            </Suspense>
          </Route>
        )}

        {isAuthorized && (
          <Route path="/ViewProducts">
            <Redirect to="/product-catalog" />
          </Route>
        )}

        {isAuthorized && (
          <Route path="/product-keys">
            <Suspense fallback={<FullPageLoadingModal />}>
              <ProductKeys />
            </Suspense>
          </Route>
        )}

        {isAuthorized && (
          <Route path="/ViewProductsKeys">
            <Redirect to="/product-keys" />
          </Route>
        )}

        {isAuthorized && (
          <Route path="/service-addresses">
            <Suspense fallback={<FullPageLoadingModal />}>
              <ServiceAddresses />
            </Suspense>
          </Route>
        )}

        {isAuthorized && (
          <Route path="/TicketDetails">
            <Suspense fallback={<FullPageLoadingModal />}>
              <TicketDetails />
            </Suspense>
          </Route>
        )}

        {isAuthorized && (
          <Route path="/ViewTickets">
            <Suspense fallback={<FullPageLoadingModal />}>
              <ViewTicketsModern />
            </Suspense>
          </Route>
        )}

        {isAuthorized && (
          <Route path="/ViewAccounts">
            <Suspense fallback={<FullPageLoadingModal />}>
              <ViewAccountsModern />
            </Suspense>
          </Route>
        )}

        {isAuthorized && (
          <Route path="/AccountDetails">
            <Suspense fallback={<FullPageLoadingModal />}>
              <AccountDetailsModern />
            </Suspense>
          </Route>
        )}

        {isAuthorized && (
          <Route path="/editAccount">
            <Suspense fallback={<FullPageLoadingModal />}>
              <EditAccountModern />
            </Suspense>
          </Route>
        )}

        {isAuthorized && (
          <Route path="/profile">
            <Suspense fallback={<FullPageLoadingModal />}>
              <ProfileModern />
            </Suspense>
          </Route>
        )}

        {isAuthorized && (
          <Route path="/inbox">
            <Suspense fallback={<FullPageLoadingModal />}>
              <InboxPage />
            </Suspense>
          </Route>
        )}

        {isAuthorized && (
          <Route path="/analytics/ai-usage">
            <Suspense fallback={<FullPageLoadingModal />}>
              <AIUsageAnalytics />
            </Suspense>
          </Route>
        )}

        {isAuthorized && (
          <Route path="/analytics/tickets">
            <Suspense fallback={<FullPageLoadingModal />}>
              <TicketsAnalytics />
            </Suspense>
          </Route>
        )}

        {isAuthorized && (
          <Route path="/settings">
            <Suspense fallback={<FullPageLoadingModal />}>
              <SettingsModern />
            </Suspense>
          </Route>
        )}

        {isAuthorized && (
          <Route path="/knowledge-base">
            <Suspense fallback={<FullPageLoadingModal />}>
              <KnowledgeBase />
            </Suspense>
          </Route>
        )}

        {isAuthorized && (
          <Route path="/AccountNetworkVisualizer">
            <Suspense fallback={<FullPageLoadingModal />}>
              <AccountNetworkVisualizer />
            </Suspense>
          </Route>
        )}

        {isAuthorized && (
          <Route path="/m365-admin-tools">
            <Suspense fallback={<FullPageLoadingModal />}>
              <M365AdminTools />
            </Suspense>
          </Route>
        )}

        {isAuthorized && (
          <Route path="/google-admin-tools">
            <Suspense fallback={<FullPageLoadingModal />}>
              <GoogleAdminTools />
            </Suspense>
          </Route>
        )}

        {isAuthorized && (
          <Route path="/tech-schedule">
            <Redirect to="/scheduling/tech" />
          </Route>
        )}

        {/* Scheduling Module Routes */}
        {isAuthorized && (
          <Route exact path="/scheduling">
            <Redirect to="/scheduling/calendar" />
          </Route>
        )}

        {/* Redirect old tech schedule route to unified calendar */}
        {isAuthorized && (
          <Route path="/scheduling/tech">
            <Redirect to="/scheduling/calendar" />
          </Route>
        )}

        {isAuthorized && (
          <Route path="/scheduling/calendar">
            <Suspense fallback={<FullPageLoadingModal />}>
              <UnifiedCalendar />
            </Suspense>
          </Route>
        )}

        {isAuthorized && (
          <Route path="/scheduling/time-off">
            <Suspense fallback={<FullPageLoadingModal />}>
              <TimeOffRequests />
            </Suspense>
          </Route>
        )}

        {isAuthorized && (
          <Route path="/scheduling/holidays">
            <Suspense fallback={<FullPageLoadingModal />}>
              <CompanyHolidays />
            </Suspense>
          </Route>
        )}

        {isAuthorized && (
          <Route path="/agents">
            <Suspense fallback={<FullPageLoadingModal />}>
              <ViewAgentsModern />
            </Suspense>
          </Route>
        )}

        {isAuthorized && (
          <Route path="/ViewAgents">
            <Redirect to="/agents" />
          </Route>
        )}

        {/* Projects Module Routes */}
        {isAuthorized && (
          <Route exact path="/projects">
            <Redirect to="/projects/list" />
          </Route>
        )}

        {isAuthorized && (
          <Route exact path="/projects/saas-products">
            <Suspense fallback={<FullPageLoadingModal />}>
              <SaasProducts />
            </Suspense>
          </Route>
        )}

        {isAuthorized && (
          <Route exact path="/projects/saas-products/:id">
            <Suspense fallback={<FullPageLoadingModal />}>
              <SaasProductDetail />
            </Suspense>
          </Route>
        )}

        {isAuthorized && (
          <Route exact path="/projects/list">
            <Suspense fallback={<FullPageLoadingModal />}>
              <ProjectsList />
            </Suspense>
          </Route>
        )}

        {isAuthorized && (
          <Route exact path="/projects/today">
            <Suspense fallback={<FullPageLoadingModal />}>
              <ProjectsToday />
            </Suspense>
          </Route>
        )}

        {isAuthorized && (
          <Route path="/projects/detail">
            <Suspense fallback={<FullPageLoadingModal />}>
              <ProjectDetail />
            </Suspense>
          </Route>
        )}

        {isAuthorized && (
          <Route path="/projects/my-tasks">
            <Suspense fallback={<FullPageLoadingModal />}>
              <MyTasks />
            </Suspense>
          </Route>
        )}

        {isAuthorized && (
          <Route path="/tasks">
            <Suspense fallback={<FullPageLoadingModal />}>
              <Tasks />
            </Suspense>
          </Route>
        )}

        {isAuthorized && (
          <Route path="/inventory">
            <Suspense fallback={<FullPageLoadingModal />}>
              <InventoryPage />
            </Suspense>
          </Route>
        )}

        {/* Sales Module Routes */}
        {isAuthorized && (
          <Route path="/sales/orders">
            <Suspense fallback={<FullPageLoadingModal />}>
              <Orders />
            </Suspense>
          </Route>
        )}

        {isAuthorized && (
          <Route path="/sales/quotes">
            <Suspense fallback={<FullPageLoadingModal />}>
              <Quotes />
            </Suspense>
          </Route>
        )}

        {isAuthorized && (
          <Route path="/leads">
            <Suspense fallback={<FullPageLoadingModal />}>
              <ViewLeads />
            </Suspense>
          </Route>
        )}

        {isAuthorized && (
          <Route path="/LeadDetails">
            <Suspense fallback={<FullPageLoadingModal />}>
              <LeadDetails />
            </Suspense>
          </Route>
        )}

        {isAuthorized && (
          <Route path="/offers">
            <Suspense fallback={<FullPageLoadingModal />}>
              <ViewOffers />
            </Suspense>
          </Route>
        )}

        {isAuthorized && (
          <Route path="/bids">
            <Suspense fallback={<FullPageLoadingModal />}>
              <ViewBids />
            </Suspense>
          </Route>
        )}

        {isAuthorized && (
          <Route path="/BidDetails">
            <Suspense fallback={<FullPageLoadingModal />}>
              <BidDetails />
            </Suspense>
          </Route>
        )}

        {isAuthorized && (
          <Route path="/highergov/discovery">
            <Suspense fallback={<FullPageLoadingModal />}>
              <HigherGovDiscovery />
            </Suspense>
          </Route>
        )}

        {isAuthorized && (
          <Route path="/monitoring/periscope">
            <Suspense fallback={<FullPageLoadingModal />}>
              <Periscope />
            </Suspense>
          </Route>
        )}

        {isAuthorized && (
          <Route path="/sonar/admin">
            <Suspense fallback={<FullPageLoadingModal />}>
              <SonarAdmin />
            </Suspense>
          </Route>
        )}

        {/* Per-host detail — must come BEFORE /sonar/docker so the
            more specific path wins. */}
        {isAuthorized && (
          <Route path="/sonar/docker/:hostname">
            <Suspense fallback={<FullPageLoadingModal />}>
              <SonarDockerHostDetail />
            </Suspense>
          </Route>
        )}

        {isAuthorized && (
          <Route path="/sonar/docker">
            <Suspense fallback={<FullPageLoadingModal />}>
              <SonarDockerServers />
            </Suspense>
          </Route>
        )}

        {isAuthorized && (
          <Route path="/sonar/nodes/:hostname">
            <Suspense fallback={<FullPageLoadingModal />}>
              <SonarNodeDetail />
            </Suspense>
          </Route>
        )}

        {isAuthorized && (
          <Route path="/sonar/kong">
            <Suspense fallback={<FullPageLoadingModal />}>
              <SonarKong />
            </Suspense>
          </Route>
        )}

        {/* Compute-stack pages awaiting their push collector. Must come before
            the /sonar catch-all so they don't fall through to the topology
            "No app with id …" empty state. */}
        {/* Per-server hardware detail — more specific than /sonar/physical, so first. */}
        {isAuthorized && (
          <Route path="/sonar/physical/:hostname">
            <Suspense fallback={<FullPageLoadingModal />}>
              <SonarPhysicalDetail />
            </Suspense>
          </Route>
        )}

        {isAuthorized && (
          <Route path="/sonar/physical">
            <Suspense fallback={<FullPageLoadingModal />}>
              <SonarComputeStub kind="physical" />
            </Suspense>
          </Route>
        )}

        {/* Facilities → Power: per-site circuit inventory + live iDRAC PSU draw.
            Must precede the /sonar catch-all. */}
        {isAuthorized && (
          <Route path="/sonar/power">
            <Suspense fallback={<FullPageLoadingModal />}>
              <SonarPower />
            </Suspense>
          </Route>
        )}

        {/* Per-host hypervisor detail — more specific than /sonar/virtualization, so first. */}
        {isAuthorized && (
          <Route path="/sonar/virtualization/:hostname">
            <Suspense fallback={<FullPageLoadingModal />}>
              <SonarVirtualizationDetail />
            </Suspense>
          </Route>
        )}

        {isAuthorized && (
          <Route path="/sonar/virtualization">
            <Suspense fallback={<FullPageLoadingModal />}>
              <SonarComputeStub kind="virtualization" />
            </Suspense>
          </Route>
        )}

        {/* Shared storage (Dell PowerVault MD SAN) — pushed by the in-VLAN
            collector via SMcli. Its own page so array health isn't mixed in
            with the hypervisor hosts on /sonar/virtualization. */}
        {/* Per-array detail — more specific than /sonar/storage, so first. */}
        {isAuthorized && (
          <Route path="/sonar/storage/:name">
            <Suspense fallback={<FullPageLoadingModal />}>
              <SonarStorageDetail />
            </Suspense>
          </Route>
        )}

        {isAuthorized && (
          <Route path="/sonar/storage">
            <Suspense fallback={<FullPageLoadingModal />}>
              <SonarComputeStub kind="storage" />
            </Suspense>
          </Route>
        )}

        {/* OSP map dashboard — more specific than /sonar/itp-fiber (handled
            by the Sonar topology catch-all), so it must come before /sonar. */}
        {isAuthorized && (
          <Route path="/sonar/itp-fiber/map">
            <Suspense fallback={<FullPageLoadingModal />}>
              <SonarOspDashboard />
            </Suspense>
          </Route>
        )}

        {/* Dedicated per-app pages — each monitored service/network gets its own
            page (topology + tools), NOT the dashboard's board+rail. Must come
            before the /sonar catch-all. voice360-dev before voice360 to avoid
            any prefix ambiguity. */}
        {isAuthorized && (
          <Route path="/sonar/voice360-dev">
            <Suspense fallback={<FullPageLoadingModal />}>
              <SonarAppPage slug="voice360-dev" />
            </Suspense>
          </Route>
        )}

        {isAuthorized && (
          <Route path="/sonar/voice360">
            <Suspense fallback={<FullPageLoadingModal />}>
              <SonarAppPage slug="voice360" />
            </Suspense>
          </Route>
        )}

        {isAuthorized && (
          <Route path="/sonar/itp360">
            <Suspense fallback={<FullPageLoadingModal />}>
              <SonarAppPage slug="itp360" />
            </Suspense>
          </Route>
        )}

        {isAuthorized && (
          <Route path="/sonar/itp-network">
            <Suspense fallback={<FullPageLoadingModal />}>
              <SonarAppPage slug="itp-network" />
            </Suspense>
          </Route>
        )}

        {isAuthorized && (
          <Route path="/sonar/saas-db">
            <Suspense fallback={<FullPageLoadingModal />}>
              <SonarAppPage slug="saas-db" />
            </Suspense>
          </Route>
        )}

        {isAuthorized && (
          <Route path="/sonar/orchestrator">
            <Suspense fallback={<FullPageLoadingModal />}>
              <SonarAppPage slug="orchestrator" />
            </Suspense>
          </Route>
        )}

        {isAuthorized && (
          <Route path="/sonar/customer-servers">
            <Suspense fallback={<FullPageLoadingModal />}>
              <SonarAppPage slug="customer-servers" />
            </Suspense>
          </Route>
        )}

        {isAuthorized && (
          <Route path="/sonar/overview">
            <Suspense fallback={<FullPageLoadingModal />}>
              <SonarFleetOverview />
            </Suspense>
          </Route>
        )}

        {/* Permalinkable device detail — must precede the /sonar catch-all. */}
        {isAuthorized && (
          <Route path="/sonar/devices/:sip_username">
            <Suspense fallback={<FullPageLoadingModal />}>
              <SonarDeviceDetail />
            </Suspense>
          </Route>
        )}

        {isAuthorized && (
          <Route path="/sonar">
            <Suspense fallback={<FullPageLoadingModal />}>
              <Sonar />
            </Suspense>
          </Route>
        )}

        {isAuthorized && (
          <Route path="/ai-agents">
            <Suspense fallback={<FullPageLoadingModal />}>
              <AIAgents />
            </Suspense>
          </Route>
        )}

        {isAuthorized && (
          <Route path="/itp-ai/:tab?">
            <Suspense fallback={<FullPageLoadingModal />}>
              <ITPAI />
            </Suspense>
          </Route>
        )}

        {isAuthorized && (
          <Route path="/accounting/:tab?/:id?">
            <Suspense fallback={<FullPageLoadingModal />}>
              <Accounting />
            </Suspense>
          </Route>
        )}

        {isAuthorized && (
          <Route path="/workbench">
            <Suspense fallback={<FullPageLoadingModal />}>
              <Workbench />
            </Suspense>
          </Route>
        )}

        {isAuthorized && (
          <Route path="/vault">
            <Suspense fallback={<FullPageLoadingModal />}>
              <Vault />
            </Suspense>
          </Route>
        )}

        {isAuthorized && (
          <Route path="/drive">
            <Suspense fallback={<FullPageLoadingModal />}>
              <Drive />
            </Suspense>
          </Route>
        )}

        {isAuthorized && (
          <Route path="/photos">
            <Suspense fallback={<FullPageLoadingModal />}>
              <Photos />
            </Suspense>
          </Route>
        )}

        {isAuthorized && (
          <Route path="/rmm">
            <Suspense fallback={<FullPageLoadingModal />}>
              <RMM />
            </Suspense>
          </Route>
        )}

        {isAuthorized && (
          <Route path="/caesargpt">
            <Suspense fallback={<FullPageLoadingModal />}><CaesarGPT /></Suspense>
          </Route>
        )}

        {/* ITP University: lesson and admin pages before the /university home */}
        {isAuthorized && (
          <Route path="/university/lesson/:lessonId">
            <Suspense fallback={<FullPageLoadingModal />}>
              <UniversityLesson />
            </Suspense>
          </Route>
        )}

        {isAuthorized && (
          <Route path="/university/admin">
            <Suspense fallback={<FullPageLoadingModal />}>
              <UniversityAdmin />
            </Suspense>
          </Route>
        )}

        {isAuthorized && (
          <Route path="/university">
            <Suspense fallback={<FullPageLoadingModal />}>
              <UniversityHome />
            </Suspense>
          </Route>
        )}

        {/* Gather — the more specific sub-pages must come before the /gather
            recordings list, which is the app's catch-all. */}
        {isAuthorized && (
          <Route path="/gather/recordings/:publicId">
            <Suspense fallback={<FullPageLoadingModal />}>
              <GatherRecordingDetail />
            </Suspense>
          </Route>
        )}

        {isAuthorized && (
          <Route path="/gather/search">
            <Suspense fallback={<FullPageLoadingModal />}>
              <GatherSearch />
            </Suspense>
          </Route>
        )}

        {isAuthorized && (
          <Route path="/gather/settings">
            <Suspense fallback={<FullPageLoadingModal />}>
              <GatherSettings />
            </Suspense>
          </Route>
        )}

        {isAuthorized && (
          <Route path="/gather/access-log">
            <Suspense fallback={<FullPageLoadingModal />}>
              <GatherAccessLog />
            </Suspense>
          </Route>
        )}

        {isAuthorized && (
          <Route path="/gather">
            <Suspense fallback={<FullPageLoadingModal />}>
              <GatherRecordings />
            </Suspense>
          </Route>
        )}

        {/* Maps app */}
        {isAuthorized && (
          <Route exact path="/maps/:viewSlug">
            <Suspense fallback={<FullPageLoadingModal />}>
              <MapsApp />
            </Suspense>
          </Route>
        )}

        {isAuthorized && (
          <Route exact path="/maps">
            <Suspense fallback={<FullPageLoadingModal />}>
              <MapsApp />
            </Suspense>
          </Route>
        )}

        {isAuthorized && (
          <Route exact path="/maps/fiber/cables/:id">
            <Suspense fallback={<FullPageLoadingModal />}>
              <MapsCableDetail />
            </Suspense>
          </Route>
        )}

        {isAuthorized && (
          <Route exact path="/maps/fiber/closures/:id/splices">
            <Suspense fallback={<FullPageLoadingModal />}>
              <MapsClosureSplices />
            </Suspense>
          </Route>
        )}

        {isAuthorized && (
          <Route exact path="/maps/fiber/closures/:id/splice-schedule/print">
            <Suspense fallback={<FullPageLoadingModal />}>
              <MapsSpliceSchedulePrint />
            </Suspense>
          </Route>
        )}

        {isAuthorized && (
          <Route exact path="/maps/fiber/circuits">
            <Suspense fallback={<FullPageLoadingModal />}>
              <MapsCircuitsList />
            </Suspense>
          </Route>
        )}

        {isAuthorized && (
          <Route exact path="/maps/fiber/circuits/:id">
            <Suspense fallback={<FullPageLoadingModal />}>
              <MapsCircuitDetail />
            </Suspense>
          </Route>
        )}

        {/* ISP Services Module Routes */}
        {isAuthorized && (
          <Route exact path="/isp">
            <Redirect to="/isp/ipam" />
          </Route>
        )}

        {isAuthorized && (
          <Route path="/isp/ipam">
            <Suspense fallback={<FullPageLoadingModal />}>
              <IPAMDashboard />
            </Suspense>
          </Route>
        )}

        {isAuthorized && (
          <Route path="/isp/fiber-smb/olt">
            <Suspense fallback={<FullPageLoadingModal />}>
              <OLTManagement />
            </Suspense>
          </Route>
        )}

        {isAuthorized && (
          <Route path="/isp/fiber-smb/ont">
            <Suspense fallback={<FullPageLoadingModal />}>
              <ONTManagement />
            </Suspense>
          </Route>
        )}

        {isAuthorized && (
          <Route path="/isp/fiber-buildings">
            <Suspense fallback={<FullPageLoadingModal />}>
              <FiberBuildings />
            </Suspense>
          </Route>
        )}

        {isAuthorized && (
          <Route path="/isp/configuration">
            <Suspense fallback={<FullPageLoadingModal />}>
              <ISPConfiguration />
            </Suspense>
          </Route>
        )}

        {isAuthorized && (
          <Route path="/help">
            <Suspense fallback={<FullPageLoadingModal />}>
              <HelpPage />
            </Suspense>
          </Route>
        )}

        {isAuthorized && (
          <Route path="/expenses">
            <Suspense fallback={<FullPageLoadingModal />}>
              <ViewExpenses />
            </Suspense>
          </Route>
        )}

        {isAuthorized && (
          <Route path="/billing/invoices">
            <Suspense fallback={<FullPageLoadingModal />}>
              <ViewInvoices />
            </Suspense>
          </Route>
        )}

        {isAuthorized && (
          <Route path="/audit-logs">
            <Suspense fallback={<FullPageLoadingModal />}>
              <AuditLogs />
            </Suspense>
          </Route>
        )}

        {isAuthorized && (
          <Route exact path="/">
            <Redirect to="/dashboard" />
          </Route>
        )}

        {isAuthorized && (
          <Route path="*">
            <Redirect to="/dashboard" />
          </Route>
        )}
      </Switch>
    </>
  );
});
